// Section « Missions » du compte : ce que le joueur a activé, et comment le retirer (CCT-328).
//
// ⚠️ LE JOUEUR NE SAVAIT PAS OÙ REGARDER. Une connexion Azure n'était visible que depuis la
// fiche d'une mission qui l'exige, et rien ne disait QUELLES missions s'en servaient. Un joueur
// voulant retirer un accès devait le deviner. C'est d'autant plus gênant que la délégation vit
// dans SA souscription : lui cacher l'inventaire de ce qu'il a accordé serait le contraire de ce
// que ce modèle promet.
//
// ⚠️ ET LA RÉVOCATION DIT CE QU'ELLE NE FAIT PAS. Retirer ici marque la connexion révoquée côté
// CloudChrono ; la délégation, elle, vit chez le joueur et lui seul peut la supprimer — nous
// n'avons que Reader. Taire cette asymétrie ferait croire à un retrait complet.
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { apiFetch } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { getThemeColors } from '../utils/themeStyles';

interface Ctrl { kind: string; ready: boolean; detail?: string; mode?: string; connectionId?: string }
interface MissionRow { missionId: number; title: string; controllers: Ctrl[]; allReady: boolean }

export default function AccountMissions() {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const c = getThemeColors(isDarkTheme);
  const { getAccessToken } = useAuth();

  const [missions, setMissions] = useState<MissionRow[] | null>(null);
  const [occupe, setOccupe] = useState('');
  const [message, setMessage] = useState('');

  const charger = useCallback(async () => {
    try {
      const d = await apiFetch<{ missions: MissionRow[] }>('/me/controllers', {
        token: getAccessToken(), auth: true, background: true,
      });
      setMissions(Array.isArray(d.missions) ? d.missions : []);
    } catch {
      // Réseau : on ne prétend pas qu'il n'y a rien à montrer.
      setMissions(null);
    }
  }, [getAccessToken]);

  useEffect(() => { void charger(); }, [charger]);

  const revoquer = async (connectionId: string) => {
    if (!connectionId) return;
    setOccupe(connectionId);
    try {
      const d = await apiFetch<any>(`/api/azure/connections/${encodeURIComponent(connectionId)}`, {
        method: 'DELETE', token: getAccessToken(), auth: true,
      });
      setMessage(d?.azureCleanup || '');
      await charger();
    } catch (e: any) {
      setMessage(String(e?.body?.detail ?? e?.message ?? e));
    } finally {
      setOccupe('');
    }
  };

  // Aucune mission n'exige d'outil : ne pas fabriquer une section vide, qui apprendrait à ne
  // plus la regarder le jour où elle aura quelque chose à dire.
  if (!missions || missions.length === 0) return null;

  const nom = (k: string) => t(k === 'cloud' ? 'controllers.cloud' : 'controllers.github');

  return (
    <View style={{ marginTop: 8 }}>
      <Text style={[s.titre, { color: c.textSecondary }]}>{t('account.missions')}</Text>
      <Text style={[s.aide, { color: c.textTertiary }]}>{t('account.missionsHelp')}</Text>

      <View style={{ gap: 10, marginTop: 12 }}>
        {missions.map((m) => (
          <View key={m.missionId}
            style={[s.carte, { backgroundColor: c.cardBackground, borderColor: c.border }]}>
            <Text style={[s.mission, { color: c.textPrimary }]}>{m.title}</Text>
            {m.controllers.map((x) => (
              <View key={x.kind} style={s.ligne}>
                <Ionicons name={x.ready ? 'checkmark-circle' : 'alert-circle'} size={15}
                  color={x.ready ? c.success : c.warning} />
                <View style={{ flex: 1 }}>
                  <Text style={[s.nom, { color: c.textPrimary }]}>{nom(x.kind)}</Text>
                  {!!x.detail && (
                    <Text style={[s.detail, { color: c.textTertiary }]} numberOfLines={1}>
                      {x.detail}
                    </Text>
                  )}
                </View>
                {/* Seule la délégation Azure se révoque d'ici : c'est la seule que CloudChrono
                    détient. Un dépôt GitHub se règle sur la fiche de sa mission, et prétendre le
                    contraire enverrait le joueur cliquer sur un bouton sans effet. */}
                {x.kind === 'cloud' && x.ready && !!x.connectionId && (
                  <TouchableOpacity onPress={() => revoquer(x.connectionId!)}
                    disabled={occupe === x.connectionId}
                    style={[s.btn, { borderColor: c.border }]}>
                    {occupe === x.connectionId
                      ? <ActivityIndicator color={c.textPrimary} />
                      : <Text style={[s.btnText, { color: c.textPrimary }]}>
                          {t('account.revoke')}
                        </Text>}
                  </TouchableOpacity>
                )}
              </View>
            ))}
          </View>
        ))}
      </View>

      {!!message && <Text style={[s.aide, { color: c.textSecondary, marginTop: 10 }]}>{message}</Text>}
    </View>
  );
}

const s = StyleSheet.create({
  titre: { fontSize: 13, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.8 },
  aide: { fontSize: 12, lineHeight: 17, marginTop: 6 },
  carte: { borderWidth: 1, borderRadius: 12, padding: 14, gap: 10 },
  mission: { fontSize: 14, fontWeight: '700' },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  nom: { fontSize: 13, fontWeight: '600' },
  detail: { fontSize: 11, marginTop: 2 },
  btn: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7 },
  btnText: { fontSize: 12, fontWeight: '600' },
});
